<template>
  <aside :class="[
    'fixed left-0 top-16 transition-all duration-300 ease-in-out bg-gray-800 min-h-[calc(100vh-4rem)]',
    modelValue ? 'w-16' : 'w-64',
    $attrs.class,
    // Drawer mobile
    isMobileView ? 'rounded-r-2xl shadow-2xl w-64 h-[calc(100vh-4rem)]' : ''
  ]" style="z-index:51">

    <nav class="mt-4 flex flex-col h-[calc(100vh-4rem)]">
      <div class="flex-grow">
        <router-link
          v-for="item in filteredMenuItems"
          :key="item.path"
          :to="item.path"
          class="block mb-2 px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition duration-200"
          :class="{ 'bg-gray-700 text-white': $route.path === item.path }"
        >
          <span class="flex items-center">
            <span v-html="item.icon" class="h-5 w-5 flex-shrink-0" :class="{ 'mr-3': !modelValue }"></span>
            <span :class="{ 'hidden': modelValue, 'opacity-100 transition-opacity duration-200': !modelValue }">{{ item.name }}</span>
          </span>
        </router-link>
      </div>
      
      <!-- Bouton de basculement du thème -->
      <div class="mb-4">
        <button 
          @click="toggleTheme" 
          :disabled="isChangingTheme"
          class="flex items-center w-full px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition duration-200"
          :class="{ 'opacity-50 cursor-not-allowed': isChangingTheme }"
        >
          <span v-if="isDarkMode" v-html="sunIcon" class="h-5 w-5 flex-shrink-0" :class="{ 'mr-3': !modelValue }"></span>
          <span v-else v-html="moonIcon" class="h-5 w-5 flex-shrink-0" :class="{ 'mr-3': !modelValue }"></span>
          <span :class="{ 'hidden': modelValue, 'opacity-100 transition-opacity duration-200': !modelValue }">
            <span v-if="isChangingTheme">Chargement...</span>
            <span v-else>{{ isDarkMode ? 'Mode clair' : 'Mode sombre' }}</span>
          </span>
        </button>
      </div>
    </nav>
  </aside>
</template>

<script>
import { defineComponent, computed, ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/themeStore'
import { storeToRefs } from 'pinia'

const menuItems = [
  {
    path: '/dashboard',
    name: 'Tableau de bord',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>`
  },
  {
    path: '/portfolio',
    name: 'Portefeuille',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
    </svg>`
  },
  {
    path: '/dividends',
    name: 'Dividendes',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 7.5a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5z" />
      <path fill-rule="evenodd" d="M1.5 4.875C1.5 3.839 2.34 3 3.375 3h17.25c1.035 0 1.875.84 1.875 1.875v9.75c0 1.036-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 011.5 14.625v-9.75zM8.25 9.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM18.75 9a.75.75 0 00-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 00.75-.75V9.75a.75.75 0 00-.75-.75h-.008zM4.5 9.75A.75.75 0 015.25 9h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9.75z" clip-rule="evenodd" />
      <path d="M2.25 18a.75.75 0 000 1.5c5.4 0 10.63.722 15.6 2.075 1.19.324 2.4-.558 2.4-1.82V18.75a.75.75 0 00-.75-.75H2.25z" />
    </svg>`
  },
  {
    path: '/transactions',
    name: 'Transactions',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7.5 3.375c0-1.036.84-1.875 1.875-1.875h.375a3.75 3.75 0 013.75 3.75v1.875C13.5 8.161 14.34 9 15.375 9h1.875A3.75 3.75 0 0121 12.75v3.375C21 17.16 20.16 18 19.125 18h-9.75A1.875 1.875 0 017.5 16.125V3.375z" />
      <path d="M13.5 12a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0v-2.25a.75.75 0 01.75-.75z" />
    </svg>`
  },
  {
    path: '/admin/users',
    name: 'Utilisateurs',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path fill-rule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" />
      <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
    </svg>`,
    requiresAdmin: true
  },
  {
    path: '/admin/api-management',
    name: 'API Management',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path fill-rule="evenodd" d="M14.447 3.027a.75.75 0 01.527.92l-4.5 16.5a.75.75 0 01-1.448-.394l4.5-16.5a.75.75 0 01.921-.526zM16.72 6.22a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L21.44 12l-4.72-4.72a.75.75 0 010-1.06zm-9.44 0a.75.75 0 010 1.06L2.56 12l4.72 4.72a.75.75 0 11-1.06 1.06L.97 12.53a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z" />
    </svg>`,
    requiresAdmin: true
  },
  {
    path: '/admin/settings',
    name: 'Paramètres',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path fill-rule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clip-rule="evenodd" />
    </svg>`,
    requiresAdmin: true
  }
]

const sunIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
</svg>`

const moonIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
  <path fill-rule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clip-rule="evenodd" />
</svg>`

export default defineComponent({
  name: 'Sidebar',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    }
  },
  setup() {
    const authStore = useAuthStore()
    const themeStore = useThemeStore()
    const isMobileView = ref(false)
    
    // Fonction pour détecter si l'écran est en mode mobile
    const checkMobileView = () => {
      isMobileView.value = window.innerWidth < 1024
    }
    
    // Initialiser et ajouter l'écouteur d'événement au chargement
    onMounted(() => {
      checkMobileView()
      window.addEventListener('resize', checkMobileView)
    })
    
    // Nettoyer l'écouteur d'événement à la destruction du composant
    onUnmounted(() => {
      window.removeEventListener('resize', checkMobileView)
    })
    
    const isAdmin = computed(() => {
      return authStore.user?.role_id === 1
    })

    const filteredMenuItems = computed(() => {
      return menuItems.filter(item => !item.requiresAdmin || isAdmin.value)
    })

    const isDarkMode = computed(() => {
      return themeStore.isDarkMode
    })
    
    const isChangingTheme = computed(() => {
      return themeStore.isChangingTheme
    })

    const toggleTheme = () => {
      if (isChangingTheme.value) return // éviter les clics multiples
      themeStore.toggleDarkMode()
    }

    return {
      filteredMenuItems,
      isDarkMode,
      isChangingTheme,
      toggleTheme,
      sunIcon,
      moonIcon,
      isMobileView
    }
  }
})
</script>

